import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
    createDeveloperEndpoint,
    getDevelopersEndpoint,
    updateDeveloperEndpoint
} from '../services/developersService';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../../hooks/useAuth';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import { fetchResponseAdapter } from '../../../adapters/fetchAdapter';
import { ErrorAlert, SuccessAlert } from '../../../models/alertModel';
import { DevManagement, DevManagementFormData } from '../models/developerManagement';


const useDevManagement = () => {
    const navigate = useNavigate();
    const [sweetAlert, setSweetAlert] = useState<boolean>(false);
    const [alertProps, setAlertProps] = useState<SuccessAlert | ErrorAlert>({
        success: true,
        title: '',
        text: '',
        onConfirm: () => setSweetAlert(false),
        onCancel: () => setSweetAlert(false),
    });
    // State to store created results
    const { getAuthToken } = useAuth();
    const token = getAuthToken();
    const [results, setResults] = useState<DevManagement[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null); // Track the ID of the row being edited
    const { callEndpoint, isLoading } = useCallApiAndLoad();
    // Initialize react-hook-form
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: '', // Default value for the name field
            description: '', // Default value for the description field
        },
        resolver: yupResolver(
            yup
                .object({
                    name: yup.string().required('Nombre es requerido'),
                    description: yup.string().required('Descripción es requerido')
                })
                .required(),
        ),
    });

    // State to manage search query
    const [searchQuery, setSearchQuery] = useState(''); // State for search query
    // Filter results based on search query
    const filteredResults = results.filter((result) =>
        result.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const onSubmit = async (data: DevManagementFormData) => {
        console.log('Submitting data:', data);
        if (editingId !== null) {
            // Call API to update the entry
            const { status } = await callEndpoint(
                updateDeveloperEndpoint(editingId.toString(), data.name, data.description, token),
            );
            if (status !== 200) {
                setAlertProps((state) => ({
                    ...state,
                    success: false,
                    danger: true,
                    title: 'Error',
                    text: 'Lo sentimos, la Desarrolladora no pudo ser actualizada',
                }));
                setSweetAlert(true);
                return;
            }
            setEditingId(null); // Clear editing state
            // Update existing entry
            setResults((prevResults) =>
                prevResults.map((result) =>
                    result.id === editingId ? { ...result, name: data.name, description: data.description } : result,
                ),
            );
            setAlertProps((state) => ({
                ...state,
                success: true,
                danger: false,
                title: 'OK',
                text: 'Desarrolladora actualizada correctamente',
            }));
            setSweetAlert(true);
            reset(); // Reset the form after submission
        } else {
            const { status } = await callEndpoint(
                createDeveloperEndpoint(data.name, data.description, token),
            );
            if (status !== 201) {
                setAlertProps((state) => ({
                    ...state,
                    success: false,
                    danger: true,
                    title: 'Error',
                    text: 'Lo sentimos, la Desarrolladora no pudo ser creada',
                }));
                setSweetAlert(true);
                return;
            }
            setAlertProps((state) => ({
                ...state,
                onConfirm: () => navigate(0),
                success: true,
                danger: false,
                title: 'OK',
                text: 'Desarrolladora creada correctamente',
            }));
            setSweetAlert(true);
        }
    };

    // navigates to the avatar upload page for the currently editing developer.
    const uploadAvatar = (id: string | null) => {
        navigate(`/investors/configs/dev-management/${id}/upload-images`);
    };

    // Handle edit button click
    const handleEdit = (id: string) => {
        if (results && results.length === 0) return;
        const resultToEdit = results.find((result) => result.id === id);
        if (resultToEdit) {
            setValue('name', resultToEdit.name);
            setValue('description', resultToEdit.description || '');
            setEditingId(id);
        }
    };

    const loadNearPlaces = async () => {
        const { status, data } = await callEndpoint(getDevelopersEndpoint(token));
        if (status !== 200) {
            return;
        }
        const response = fetchResponseAdapter(data);
        setResults(response.data);
    };

    useEffect(() => {
        loadNearPlaces().catch((e) => console.error(e));
    }, []);


    return {
        control,
        errors,
        editingId,
        reset,
        handleSubmit,
        setEditingId,
        onSubmit,
        isLoading,
        sweetAlert,
        alertProps,
        searchQuery,
        setSearchQuery,
        filteredResults,
        results,
        handleEdit,
        uploadAvatar
    }

};

export default useDevManagement;

